import { CmmOrderTableRowData } from "../../../../data-model/cmmOrderTableRowData";
import { ModalId } from "../../../../enums/modalId";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import {
  formatDaysSupply,
  formatDosageQuantity,
  formatDrugConfidenceScore,
  getCorrectText,
  isEmpty,
} from "../../utils/getCorrectText";
import InternalChildCard from "../internalChildCard/internalChildCard";
import InternalChildCardMedical from "../internalChildCard/internalChildCardMedical";
import ModalContainer from "../modalContainer/modalContainer";

interface CmmOrderExpandableRowProps {
  rowData: CmmOrderTableRowData;
}

const CmmOrderExpandableRow = ({ rowData }: CmmOrderExpandableRowProps) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const emptyDisplay = orgId === OrganizationType.ASTERA ? "" : "N/A";
  const secondaryEmptyDisplay = orgId === OrganizationType.ASTERA ? "" : "--";

  const dosageQuantityText = formatDosageQuantity(
    rowData?.drugQuantity,
    rowData?.drugQuantityQualifier,
  );
  const daysSupplyText = formatDaysSupply(rowData?.drugDaysSupply);

  const drugConfidenceScoreText = formatDrugConfidenceScore(
    rowData?.drugFetchedFrom === "llm" ? "1" : rowData?.drugConfidenceScore,
  );
  const insuranceCorrectText = getCorrectText(rowData);
  const showInsuranceText =
    orgId === OrganizationType.ASTERA && insuranceCorrectText?.includes("N/A")
      ? ""
      : insuranceCorrectText;

  return (
    <div className="flex w-full">
      <div className="border-primaryGray13 flex w-full flex-row items-center gap-2 bg-tertiaryBlue-12">
        <div className="left-half flex w-1/2">
          <InternalChildCardMedical
            title={`Medication ${rowData?.prescriptionData?.is_related_drug_match ? "(Related Drug)" : ""}`}
            primaryDescription={[
              isEmpty(rowData?.prescriptionData?.prescription_date)
                ? emptyDisplay
                : rowData?.prescriptionData?.prescription_date,
              isEmpty(rowData?.prescriptionData?.drug_name)
                ? emptyDisplay
                : rowData?.prescriptionData?.drug_name,
            ]}
            secondaryDescription={[
              `${rowData?.medicationDecisionResult?.similarity_score != null ? rowData.medicationDecisionResult.similarity_score : "--"}`,
              isEmpty(rowData?.drugName) ? emptyDisplay : rowData?.drugName,
            ]}
            showIcon={true}
            modalId={ModalId.EXPANDABLE_TABLE_ROW_MEDICATION_MODAL}
            rowData={rowData}
          />
          <div className="w-full">
            <InternalChildCard
              title="Insurance"
              primaryDescription={[
                isEmpty(rowData?.formPickedFlag)
                  ? emptyDisplay
                  : rowData?.formPickedFlag,
                showInsuranceText,
              ]}
              secondaryDescription={[
                isEmpty(rowData?.patientMemberId)
                  ? secondaryEmptyDisplay
                  : rowData?.patientMemberId,
                isEmpty(rowData?.formName)
                  ? secondaryEmptyDisplay
                  : rowData?.formName,
              ]}
              showIcon={true}
              isPrimaryInformation={true}
              modalId={ModalId.EXPANDABLE_TABLE_ROW_INSURANCE_MODAL}
              rowData={rowData}
            />
          </div>
        </div>

        <div className="right-half flex w-1/2">
          <InternalChildCard
            title="Dosage"
            primaryDescription={[
              drugConfidenceScoreText === "N/A" ||
              drugConfidenceScoreText === "--"
                ? emptyDisplay
                : drugConfidenceScoreText,
              isEmpty(rowData?.drugFetchedFrom)
                ? emptyDisplay
                : rowData?.drugFetchedFrom,
            ]}
            secondaryDescription={[
              dosageQuantityText === "--"
                ? secondaryEmptyDisplay
                : dosageQuantityText,
              daysSupplyText === "--" ? secondaryEmptyDisplay : daysSupplyText,
            ]}
            modalId={ModalId.EXPANDABLE_TABLE_ROW_DOSAGE_MODAL}
            showIcon={true}
            isPrimaryInformation={false}
            rowData={rowData}
          />

          <InternalChildCard
            title="Primary ICD"
            primaryDescription={[
              isEmpty(rowData?.primaryDiagnosesData?.confidence_score)
                ? emptyDisplay
                : rowData?.primaryDiagnosesData?.confidence_score,
              isEmpty(rowData?.primaryDiagnosesData?.source)
                ? emptyDisplay
                : rowData?.primaryDiagnosesData?.source,
            ]}
            secondaryDescription={[
              isEmpty(rowData?.primaryDiagnoses)
                ? secondaryEmptyDisplay
                : rowData?.primaryDiagnoses,
            ]}
            showIcon={true}
            isPrimaryInformation={false}
            modalId={ModalId.EXPANDABLE_TABLE_ROW_DIAGNOSIS_MODAL}
            rowData={rowData}
            showIcdDescriptionTooltip={true}
            icdCode={rowData?.primaryDiagnoses}
          />

          <InternalChildCard
            title="Secondary ICD"
            primaryDescription={[
              isEmpty(rowData?.secondaryDiagnosesData?.confidence_score)
                ? emptyDisplay
                : rowData?.secondaryDiagnosesData?.confidence_score,
              isEmpty(rowData?.secondaryDiagnosesData?.source)
                ? emptyDisplay
                : rowData?.secondaryDiagnosesData?.source,
            ]}
            secondaryDescription={[
              isEmpty(rowData?.secondaryDiagnoses)
                ? secondaryEmptyDisplay
                : rowData?.secondaryDiagnoses,
            ]}
            showIcon={true}
            isPrimaryInformation={false}
            modalId={ModalId.EXPANDABLE_TABLE_ROW_SECONDARY_DIAGNOSIS_MODAL}
            rowData={rowData}
            showIcdDescriptionTooltip={true}
            icdCode={rowData?.secondaryDiagnoses}
          />

          {/* <InternalChildCard
            title="DAW"
            primaryDescription={["N/A"]}
            secondaryDescription={["--"]}
            showIcon={false}
            isPrimaryInformation={false}
          /> */}
        </div>
      </div>
      <ModalContainer rowData={rowData} />
    </div>
  );
};

export default CmmOrderExpandableRow;
